import React, { useCallback, useEffect, useState, useMemo } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import { Tab, Tabs } from '@app/react-common/components/tabs/Tabs';
import { useAppContext } from '@app/react-common/AppContext';
import useCheckouts from './hooks/useCheckouts';
import { getCheckoutStatus, moveLiveCheckoutToTopOfTheList } from '@app/react-common/utils/CheckoutUtils';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { AbTest } from '../../models/AbTest';
import { AbTestVariant } from '../../models/AbTestVariant';
import { TrafficAllocationVariant } from '@app/react-common/components/traffic-allocation/TrafficAllocationShared';
import { AbTestWidgetType, AbTestTypeCategory } from '../../models/AbTestCreationType';
import { ShippingMethodsAllocationSection } from './components/ShippingMethodsAllocationSection';
import { AbTestFormData, getInitialFormData } from './shared/abTestModalUtils';
import { AbTestModalHeader } from './shared/AbTestModalHeader';
import { AbTestModalDetailsTab } from './shared/AbTestModalDetailsTab';
import { AbTestModalRulesTab } from './shared/AbTestModalRulesTab';
import { AbTestModalTrafficAllocationTab } from './shared/AbTestModalTrafficAllocationTab';
import { useAbTestModalCommon } from './shared/useAbTestModalCommon';

interface AbTestModalShippingGroupsProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  saveTest?: (formData: AbTest) => Promise<number | undefined>;
  existingAbTest?: AbTest;
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  runningAbTestCheckoutIds: number[];
  onTestTypeChange?: (testType: AbTestTypeCategory) => void;
}

const TAB_ORDER = ['Details', 'Rules', 'Traffic allocation'];

export const AbTestModalShippingGroups: React.FC<AbTestModalShippingGroupsProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  saveTest,
  existingAbTest,
  mode,
  runningAbTestCheckoutIds,
  onTestTypeChange,
}) => {
  const { shop$, showToast } = useAppContext();
  const [activeTab, setActiveTab] = useState('Details');
  const [shop, setShop] = useState<Shop>(null);
  const [trafficAllocationValid, setTrafficAllocationValid] = useState(false);
  const [rulesValid, setRulesValid] = useState(mode === 'create' ? false : true);
  const [statisticalSignificanceError, setStatisticalSignificanceError] = useState<string | undefined>(undefined);
  const [fullFormValid, setFullFormValid] = useState(false);
  const [showInvalidStep, setShowInvalidStep] = useState(0);
  const [formData, setFormData] = useState<AbTestFormData>(getInitialFormData(existingAbTest));
  const { checkouts } = useCheckouts();

  // Reset validation state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowInvalidStep(0);
    }
  }, [isOpen]);

  const {
    getNextTab,
    getPreviousTab,
    handleInputChange,
    setFormDataValue,
    handleSave,
    handleTabChange,
    handleDetailsValidation,
    handleRulesValidation,
    filteredRulesBasedOnTestTypeCatagory,
    checkoutApi,
  } = useAbTestModalCommon({
    mode,
    formData,
    setFormData,
    rulesValid,
    setRulesValid,
    setStatisticalSignificanceError,
    statisticalSignificanceError,
    widgetType: AbTestWidgetType.CHECKOUT,
    shop,
    existingAbTest,
    saveTest,
    onSaveSuccess,
    onClose,
    activeTab,
    setActiveTab,
    TAB_ORDER,
  });

  // Memoize checkout variants mapping to avoid unnecessary recalculations
  const checkoutVariants = useMemo(() => {
    if (!checkouts) return [];
    const sortedCheckouts = [...checkouts];
    moveLiveCheckoutToTopOfTheList(sortedCheckouts, []);
    return sortedCheckouts.map((checkout, idx) => {
      const existingAbTestVariant: AbTestVariant = existingAbTest?.abTestVariants?.find((variant) =>
        variant.abTestWidgets?.find(
          (widget) =>
            widget.type === AbTestWidgetType.CHECKOUT &&
            // Convert both to Number to handle BIGINT values that may be serialized as strings from MySQL
            // Number(widget.externalId) === Number(checkout.id),
            widget.externalId === checkout.id,
        ),
      );

      const trafficAllocationVariant: TrafficAllocationVariant = {
        apiId: null,
        widgetExternalId: checkout.id,
        name: checkout.name,
        description: checkout.description,
        percentageTraffic: mode == 'create' && idx < 2 ? 50 : 0,
        checkoutStatus: getCheckoutStatus(checkout, runningAbTestCheckoutIds),
        isSelected: mode === 'create' ? idx < 2 : false,
        widgetApiId: undefined,
      };
      if (existingAbTestVariant) {
        trafficAllocationVariant.isSelected = true;
        trafficAllocationVariant.percentageTraffic = existingAbTestVariant.percentageTraffic;
        trafficAllocationVariant.apiId = existingAbTestVariant.id;
        const widget = existingAbTestVariant.abTestWidgets?.find((x) => x.type === AbTestWidgetType.CHECKOUT);
        trafficAllocationVariant.widgetApiId = widget?.id;
      }

      return trafficAllocationVariant;
    });
  }, [checkouts, existingAbTest, mode, runningAbTestCheckoutIds]);

  // Populate form data from checkouts
  useEffect(() => {
    if (checkoutVariants.length > 0) {
      setFormData((prevData) => ({
        ...prevData,
        abTestVariants: checkoutVariants,
      }));
    }
  }, [checkoutVariants]);

  useEffect(() => {
    const subscription = shop$.subscribe((shop) => {
      if (shop) {
        setShop(shop);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [shop$]);

  useEffect(() => {
    setFullFormValid(trafficAllocationValid && rulesValid && formData?.name?.length > 0);
  }, [formData.name, trafficAllocationValid, rulesValid]);

  const isNextTabTokenForShopify = useCallback(
    (currentTabName: string): boolean => {
      const nextTab = getNextTab(currentTabName);
      return nextTab === 'Token for Shopify';
    },
    [getNextTab],
  );

  const handleTabChangeWithValidation = useCallback(
    (tab: string) => {
      handleTabChange(tab, setShowInvalidStep);
    },
    [handleTabChange, setShowInvalidStep],
  );

  const handleDetailsValidationCallback = useCallback(
    (tab: string) => handleDetailsValidation(tab, setShowInvalidStep),
    [handleDetailsValidation, setShowInvalidStep],
  );

  const handleRulesValidationCallback = useCallback(
    (tab: string) => handleRulesValidation(tab, setShowInvalidStep),
    [handleRulesValidation, setShowInvalidStep],
  );

  const renderTabContent = useCallback(
    (tabName: string) => {
      switch (tabName) {
        case 'Details':
          return (
            <AbTestModalDetailsTab
              formData={formData}
              mode={mode}
              showInvalidStep={showInvalidStep}
              statisticalSignificanceError={statisticalSignificanceError}
              setStatisticalSignificanceError={setStatisticalSignificanceError}
              setFormDataValue={setFormDataValue}
              handleInputChange={handleInputChange}
              onClose={onClose}
              getNextTab={getNextTab}
              handleTabChange={handleTabChangeWithValidation}
              handleDetailsValidation={handleDetailsValidationCallback}
            />
          );
        case 'Rules':
          return (
            <AbTestModalRulesTab
              formData={formData}
              mode={mode}
              showInvalidStep={showInvalidStep}
              rulesValid={rulesValid}
              widgetType={AbTestWidgetType.CHECKOUT}
              shop={shop}
              existingAbTest={existingAbTest}
              filteredRulesBasedOnTestTypeCatagory={filteredRulesBasedOnTestTypeCatagory}
              checkoutApi={checkoutApi}
              showToast={showToast}
              setFormData={setFormData}
              setRulesValid={setRulesValid}
              getPreviousTab={getPreviousTab}
              getNextTab={getNextTab}
              setActiveTab={setActiveTab}
              handleTabChange={handleTabChangeWithValidation}
              handleRulesValidation={handleRulesValidationCallback}
              handleSave={handleSave}
            />
          );
        case 'Traffic allocation':
          return (
            <AbTestModalTrafficAllocationTab
              widgetType={AbTestWidgetType.CHECKOUT}
              mode={mode}
              showInvalidStep={showInvalidStep}
              trafficAllocationValid={trafficAllocationValid}
              fullFormValid={fullFormValid}
              getPreviousTab={getPreviousTab}
              getNextTab={getNextTab}
              isNextTabTokenForShopify={isNextTabTokenForShopify}
              setActiveTab={setActiveTab}
              handleSave={handleSave}
              trafficAllocationContent={
                <ShippingMethodsAllocationSection
                  formData={formData}
                  shop={shop}
                  setFormData={setFormData}
                  setTrafficAllocationValid={setTrafficAllocationValid}
                  mode={mode}
                />
              }
              setShowInvalidStep={setShowInvalidStep}
              onSaveSuccess={onSaveSuccess}
            />
          );
        default:
          return null;
      }
    },
    [
      formData,
      mode,
      showInvalidStep,
      statisticalSignificanceError,
      setStatisticalSignificanceError,
      setFormDataValue,
      handleInputChange,
      onClose,
      getNextTab,
      handleTabChangeWithValidation,
      handleDetailsValidationCallback,
      handleRulesValidationCallback,
      rulesValid,
      shop,
      existingAbTest,
      filteredRulesBasedOnTestTypeCatagory,
      checkoutApi,
      showToast,
      setFormData,
      setRulesValid,
      getPreviousTab,
      setActiveTab,
      handleSave,
      trafficAllocationValid,
      fullFormValid,
      isNextTabTokenForShopify,
    ],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="ab-test-modal">
        <AbTestModalHeader
          mode={mode}
          testTypeCategory={AbTestTypeCategory.SHIPPING_RATES}
          onClose={onClose}
          onTestTypeChange={onTestTypeChange}
        />
        <Tabs
          activeTab={activeTab}
          setActiveTab={handleTabChangeWithValidation}
          clickableTabs={true}
          horizontalLineUnderTabs={true}
          scrollable={true}>
          {TAB_ORDER.map((tabName) => (
            <Tab key={tabName} label={tabName} showTab={true}>
              {renderTabContent(tabName)}
            </Tab>
          ))}
        </Tabs>
      </div>
    </Modal>
  );
};
